/**
 * Extracts a clean filename from a URL, handling both regular URLs and signed URLs
 * @param url - The URL to extract filename from
 * @returns Clean filename or fallback name
 */
export const extractFilenameFromUrl = (url: string): string => {
  try {
    // Parse the URL to handle query parameters properly
    const urlObj = new URL(url);

    // Get the pathname and extract the last segment
    const pathSegments = urlObj.pathname.split('/');
    let lastSegment = pathSegments[pathSegments.length - 1];

    // If we have a filename with extension, return it
    if (lastSegment?.includes('.')) {
      lastSegment = lastSegment.replace(/^[a-z0-9]*-/, ''); // Remove the initial random string from the filename
      // Decode any URL encoding
      return decodeURIComponent(lastSegment);
    }

    // Fallback: try to find filename in query parameters (for some signed URL formats)
    const responseContentDisposition = urlObj.searchParams.get('response-content-disposition');
    if (responseContentDisposition) {
      const filenameMatch = responseContentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch?.[1]) {
        return filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Another fallback: look for filename in the path before query parameters
    const pathWithoutQuery = urlObj.pathname;
    const segments = pathWithoutQuery.split('/').filter(Boolean);

    // Look for segments that look like filenames (contain dots)
    for (let i = segments.length - 1; i >= 0; i -= 1) {
      if (segments[i].includes('.')) {
        return decodeURIComponent(segments[i]);
      }
    }

    // Final fallback
    return lastSegment || 'archivo';
  } catch (_error) {
    // If URL parsing fails, try simple string manipulation
    const parts = url.split('?')[0].split('/');
    const filename = parts[parts.length - 1];
    return filename || 'archivo';
  }
};

/**
 * Gets file extension from a URL
 * @param url - The URL to extract extension from
 * @returns File extension (without dot) or empty string
 */
export const getFileExtensionFromUrl = (url: string): string => {
  try {
    const filename = extractFilenameFromUrl(url);
    const lastDotIndex = filename.lastIndexOf('.');

    if (lastDotIndex !== -1 && lastDotIndex < filename.length - 1) {
      return filename.substring(lastDotIndex + 1).toLowerCase();
    }

    return '';
  } catch (_error) {
    return '';
  }
};
